import { z } from "zod";
import { email, password, phoneE164, timezone, trimmedString } from "./common.js";

/**
 * Auth request shapes.
 *
 * Note what is NOT here: there is no login, refresh or password-reset endpoint
 * on the API. Supabase owns credentials, sessions and token rotation; the
 * clients talk to it directly with the Supabase SDK and send the resulting
 * access token to Express.
 *
 * The sign-up schemas below are therefore **client-side** - they shape the four
 * forms and the `supabase.auth.*` calls behind them, and the API never sees any
 * of them. The one the API does see is `createWorkspaceSchema`, the last of the
 * four, because creating a company is a tenancy decision and tenancy decisions
 * belong to us.
 */

export const signInSchema = z.object({
  email,
  password: z.string().min(1, "Enter your password"),
});
export type SignInInput = z.infer<typeof signInSchema>;

/**
 * How many digits are in an emailed verification code.
 *
 * Exported rather than written as `6` in the input component and again in the
 * schema, because GoTrue's own token length is configuration - if it ever stops
 * being six, a hard-coded input silently accepts a code it cannot submit.
 */
export const EMAIL_CODE_LENGTH = 6;

/**
 * Digits in a TOTP code.
 *
 * Six by RFC 6238, and separate from `EMAIL_CODE_LENGTH` despite being the same
 * number today: one is GoTrue's configurable mail token, the other is an
 * authenticator app's fixed output. Sharing the constant would make a change to
 * the first silently break the second.
 */
export const TOTP_CODE_LENGTH = 6;

/**
 * Self sign-up, step one: name and email, and nothing else.
 *
 * ── Why there is no password here ───────────────────────────────────────────
 *
 * The old version of this schema asked for name, email, company and a
 * confirmed password on one screen, then emailed a `/auth/v1/verify` link to
 * activate it. That had two failure modes that were not fixable by validation:
 *
 *   1. The emailed link is a single-use GET. Mail security scanners (Outlook
 *      Safe Links and every corporate equivalent) fetch every URL in a message,
 *      which **consumes the token before the human clicks it** - the user then
 *      gets `otp_expired` on a link that is thirty seconds old.
 *   2. With `flowType: "pkce"`, the code verifier lives in the localStorage of
 *      the browser that started the sign-up. Open the mail on a phone and the
 *      exchange cannot complete at all.
 *
 * A typed code has neither problem: a scanner's GET cannot consume it because
 * verification is a POST from our own page, and there is no per-browser verifier
 * to lose, so the mail can be read anywhere.
 *
 * That also makes the split the product wanted honest rather than cosmetic. The
 * password is set in step three, **after** the address is proven, so an
 * abandoned sign-up leaves an unverified identity with no credential rather than
 * a working password on an address nobody has demonstrated they can read. The
 * company name is asked for in step four, where it is actually used.
 *
 * Social sign-in skips steps one to three: the provider proves the address and
 * owns the credential, so those users land on step four directly.
 */
export const startSignUpSchema = z.object({
  email,
  full_name: trimmedString(2, 120),
  /**
   * Optional, and the only optional field in the sign-up flow.
   *
   * It is asked for here rather than left to the profile page because of what it
   * is for: a stop-work escalation reaches a supervisor by phone, and the
   * account most likely to be missing a number is the one nobody has been back
   * to edit. Asking once, at the point somebody is already typing their details,
   * costs one field on one screen.
   *
   * It stays optional because it is not needed to create an account and a
   * required field is a wall in front of a funnel. `phoneE164` is the whole of
   * the rule and it is shared with `inviteUserSchema.phone` and
   * `updateProfileSchema.phone`, the two other ways the same column gets
   * written - `users.phone` is unconstrained in Postgres, so those three
   * schemas have to agree and now do so by being one thing.
   */
  phone: phoneE164.optional(),
});
export type StartSignUpInput = z.infer<typeof startSignUpSchema>;

/**
 * Self sign-up, step two: the code from the email.
 *
 * Non-digits are stripped before validation rather than rejected. People paste
 * codes with a stray space out of a mail client, and failing that with "digits
 * only" is a rule the user did not break.
 */
export const emailCodeSchema = z.object({
  email,
  code: z
    .string()
    .transform((value) => value.replace(/\D/g, ""))
    .refine((value) => value.length === EMAIL_CODE_LENGTH, `Enter all ${EMAIL_CODE_LENGTH} digits`),
});
export type EmailCodeInput = z.infer<typeof emailCodeSchema>;

/**
 * Self sign-up, step three: choose a password.
 *
 * Same shape as `resetPasswordSchema` and deliberately a separate name. They
 * are the same fields today, but one is "set your first credential" and the
 * other is "replace the one you have"; collapsing them means the first rule
 * that applies to only one of the two has to un-collapse them again.
 */
export const setPasswordSchema = z
  .object({
    password,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });
export type SetPasswordInput = z.infer<typeof setPasswordSchema>;

/**
 * Self sign-up, step two: `POST /auth/workspace`.
 *
 * Note the absence of a `company_id` and of a `role`. Both are deliberate and
 * both are load-bearing: without a company id there is no way to express
 * "join Acme Utilities", and without a role there is no way to ask for
 * superadmin. The caller becomes the `admin` of a company that did not exist a
 * moment ago, and that is the only outcome this endpoint can produce.
 */
export const createWorkspaceSchema = z.object({
  company_name: trimmedString(2, 120),
  /**
   * Their first crew or location. Asked for rather than defaulted to something
   * like "Head office", because every assessment is filed against a site and a
   * placeholder name is one nobody ever goes back to fix.
   */
  site_name: trimmedString(2, 120),
  timezone,
  /** Optional: an OAuth provider usually supplied one already. */
  full_name: trimmedString(2, 120).optional(),
});
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceSchema>;

export const forgotPasswordSchema = z.object({ email });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    password,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/** `PATCH /auth/me` - the fields a user may change about themselves. */
export const updateProfileSchema = z.object({
  full_name: trimmedString(2, 120).optional(),
  phone: phoneE164.nullable().optional(),
  timezone: z.string().trim().min(1).max(64).optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
